/*=== CORE IMPORTS ===*/
import { html, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { Q1MemberSelectListStyles } from './q1-member-select-list-styles.js';
import { FormInputMixin } from '../../modules/form-input-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../q1-member-chip/q1-member-chip.js';

/*=== MODULES ===*/
import Fuse from 'fuse.js'

/*=== CONSTANTS ===*/

// None yet.

/*=== COMPONENT CLASS ===*/

export class Q1MemberSelectList extends FormInputMixin(LitElement) {
  static styles = [
    Q1MemberSelectListStyles,
  ];

  static properties = {
    ...super.properties, // label, name, value, disabled, required, valid, helpText, _internals

    /**
     * Array of members that are available for search & selection.
     */
    memberList: { type: Array },

    /**
     * Maximum number of members that can be selected.
     * A value of 0 means unlimited.
     * Defaults to 0.
     */
    maxlength: { type: Number },

    /**
     * Number of rows to display in the member list.
     * 
     * Defaults to 5.
     */
    rows: { type: Number },

    /**
     * Placeholder text for the search input.
     * Defaults to `Search q1 Network members...`.
     */
    placeholder: { type: String, attribute: 'placeholder' },

    /**
     * Returns the array of selected members.
     * 
     * Note: This is overwritten from FormInputMixin to return an array instead of a string.
     */
    value: { type: Array },

    /**
     * Specifies the field name which will be used to determine if a list member is disabled.
     * Disabled members will show up in the search results but cannot be selected.
     * A `memberList` item will be considered disabled if the value of this field is truthy.
     * 
     * Default Field: `disabled`.
     */
    disabledField: { type: String, attribute: 'disabled-field' },

    /**
     * Specifies the field name which contains a label displayed next to disabled list members.
     * 
     * Default Field: `disabled_label`.
     * Default Label (if field is empty for an item): `Unavailable`.
     */
    disabledLabelField: { type: String, attribute: 'disabled-label-field' },

    /**
     * Specifies the field name containing an explanatory tooltip attached to the disabled label.
     * 
     * Default Field: `disabled_tooltip`.
     * Default Tooltip (if field is empty for an item): NONE.
     */
    disabledTooltipField: { type: String, attribute: 'disabled-tooltip-field' },

    /*=== INTERNAL STATE ===*/

    /** Internal, do not use. */
    search: { type: Object },

    /** Search text for filtering the member list. */
    searchText: { type: String },
  };

  constructor() {
    super();

    this.memberList = [];
    this.maxlength = 0;
    this.rows = 5;
    this.placeholder = 'Search q1 Network members...';

    this.value = [];

    this.disabledField = 'disabled';
    this.disabledLabelField = 'disabled_label';
    this.disabledTooltipField = 'disabled_tooltip';
    
    this.search = null;
    this.searchText = '';

    // Configure Fuse.js
    this.fuse = new Fuse(this.memberList, {
      keys: ['nameInternal', 'number'], // Search in the 'name' field
      threshold: 0.3, // Controls fuzziness (0 = exact match, 1 = very loose)
      ignoreLocation: true // Matches anywhere in the string
    });
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    this.updateFormValue();
  }

  firstUpdated() {
    this.updateComplete.then(() => {
      this.search = this.renderRoot.getElementById('search');
  
      this.updateValidity(); // can't run until this.search exists
    });
  }

  updated(changedProps) {
    if (changedProps.has('memberList')) {
      this.fuse.setCollection(this.memberList);
    }
    if (changedProps.has('rows')) {
      this.style.setProperty('--rows', this.rows || 5);
    }
  }

  /*=== ACTIONS ===*/

  toggleSelected(event) {
    const memberId = event.currentTarget.dataset.memberId;
    const member = this.memberList.find(m => m.id === memberId);

    if (this.maxlength > 0 && this.selectedMembers.length >= this.maxlength && !member.selected)
      return; // Do not allow selection if max length is reached

    if (member && (!this.disabledField || !member[this.disabledField])) {
      member.selected = !member.selected;

      // FIXME >> Should check if max length is reached before toggling.
      
      this.requestUpdate(); // Trigger a re-render

      this.handleChange(); // Update the value and validity
    }
  }

  /*=== STANDARD FORM IMPLEMENTATION ===*/

  formResetCallback() {
    this.searchText = '';
    this.memberList.forEach(member => member.selected = false);
    this.handleChange();
  }

  // FIXME >> Should be moved partially to the mixin somehow. Wait for multiple examples.
  updateValidity() {
    if (this.required && !this.value?.length) {
      this._internals.setValidity(
        { valueMissing: true },
        'At least one member is required',
        this.search,
      );
    } else if (this.maxlength > 0 && this.value?.length > this.maxlength) {
      this._internals.setValidity(
        { tooLong: true },
        `You can select up to ${this.maxlength} members only`,
        this.search,
      );
    } else {
      this._internals.setValidity({}, '', this.search);
    }
  }

  /*=== HANDLERS ===*/

  handleSearchInput(event) {
    this.searchText = event.target.value || '';
  }

  handleChange() {
    this.value = this.selectedMembers;

    // Update validity based on the current state of the component
    this.valid = this.required ? this.value?.length > 0 : true;
    if (this.maxlength > 0 && this.value?.length > this.maxlength) {
      this.valid = false;
    }

    this.updateFormValue();
    this.updateValidity();

    super.handleChange(); // dispatches the event
  }
  
  /*=== GETTERS ===*/

  get filteredMemberList() {
    if (!this.searchText?.trim()) return this.memberList;

    return this.fuse.search(this.searchText).map(result => result.item);
  }

  get selectedMembers() {
    return this.memberList.filter(member => member.selected);
  }

  /*=== TEMPLATES ===*/

  render() {
    return html`
      <div id="component-base" part="base">
        ${this.labelTemplate}

        <div id="selected-wrapper">
          ${this.selectedMembersTemplate}

          ${this.maxLengthTemplate}
        </div>
        
        <div id="search-wrapper">
          ${this.searchInputTemplate}
          
          <div id="member-list">
            ${this.filteredMemberListTemplate}
          </div>
        </div>
      </div>
    `;
  }

  get labelTemplate() {
    if (!this.label?.length) return '';

    return html`
      <label for="search">${this.label}</label>
    `;
  }

  get selectedMembersTemplate() {
    if (!this.selectedMembers.length) return html`
      <div id="selected-member-placeholder">
        <span>Click on members in the list below to select them.</span>
      </div>
    `;

    return html`
      <div id="selected-member-list">
        ${repeat(this.selectedMembers, (member) => member.id, member => html`
          <div class="selected-member-item">
            <q1-member-chip
              .member=${member}
              signed-in no-links
            ></q1-member-chip>

            <item-spacer></item-spacer>

            <sl-icon-button
              library="material"
              name="close"
              label="Deselect"
              @click=${this.toggleSelected}
              data-member-id=${member.id}
            ></sl-icon-button>
          </div>
        `)}
      </div>
    `;
  }

  get maxLengthTemplate() {
    if (!this.maxlength) return '';

    const currentUsage = this.selectedMembers.length / this.maxlength;
    if (currentUsage < 0.5) return '';

    const itemClass = currentUsage >= 1
     ? 'danger'
     : currentUsage >= 0.7 ? 'warning' : '';

    return html`
      <div id="maxlength-info" class=${itemClass}>
        You've selected ${this.selectedMembers.length} of ${this.maxlength} max members.
      </div>
    `;
  }

  get searchInputTemplate() {
    return html`
      <sl-input
        id="search"
        name="searchText"
        value=${this.searchText}
        placeholder=${this.placeholder}
        @sl-input=${this.handleSearchInput}
        help-text=${this.helpText}
        ?disabled=${this.disabled}
        clearable
      >
        <sl-icon slot="prefix" library="material" name="search"></sl-icon>
      </sl-input>
    `;
  }

  get filteredMemberListTemplate() {
    if (!this.filteredMemberList.length) return html`
      <div id="search-list-placeholder">
        <span>No matching members found.</span>
      </div>
    `;

    return html`
      ${repeat(this.filteredMemberList, (member) => member.id, member => html`
        <div
          class=${classMap({
            'search-member-item': true,
            selected: member.selected,
            disabled: this.disabledField && member[this.disabledField],
          })}
          @click=${this.toggleSelected}
          data-member-id=${member.id}
        >
          <q1-member-chip
            .member=${member}
            extra-fields="number"
            icon=${member.level_icon}
            icon-tooltip=${member.level_label}
            ?disabled=${this.disabledField && member[this.disabledField]}
            signed-in no-links
          ></q1-member-chip>

          <item-spacer></item-spacer>

          ${member.selected ? html`
            <span class="selected label">
              <span>Selected</span>
              <sl-icon library="material" name="check_circle"></sl-icon>
            </span>
          ` : html`
            ${!this.disabledField || !member[this.disabledField] ? '' : html`
              <span class="disabled label">
                ${member[this.disabledLabelField] || 'Unavailable'}
                ${member[this.disabledTooltipField] && member[this.disabledTooltipField] ? html`
                  <sl-tooltip content="${member[this.disabledTooltipField]}">
                    <sl-icon library="material" name="disabled_by_default"></sl-icon>
                  </sl-tooltip>
                ` : ''}
              </span>
            `}
          `}
        </div>
      `)}
    `;
  }
}

customElements.define('q1-member-select-list', Q1MemberSelectList);
